import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import ChartModal from '../components/masterchef/ChartModal';
import ModeStatsCard from '../components/masterchef/ModeStatsCard';
import PortfolioPanel from '../components/masterchef/PortfolioPanel';
import useDefaultTradingMode from '../hooks/useDefaultTradingMode';
import {
  CHAIN_LABELS, CHAIN_COLORS, STRATEGY_LABELS,
  formatNum, formatTime, getStrategyMeta, getNativeSymbol,
} from '../utils/masterChefUtils';

function BreakdownTable({ title, data, nativeByKey = false }) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {nativeByKey && (
          <p className="text-[10px] text-gray-400 mt-0.5">Invested and P&L in each chain&apos;s native token</p>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <th className="text-left px-4 py-2">Group</th>
              <th className="text-right px-2 py-2">Open</th>
              <th className="text-right px-2 py-2">Closed</th>
              {nativeByKey && <th className="text-right px-2 py-2">Invested</th>}
              <th className="text-right px-2 py-2">Win Rate</th>
              <th className="text-right px-2 py-2">P&L</th>
              <th className="text-right px-2 py-2">Today</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data).map(([key, s]) => {
              const sym = nativeByKey && CHAIN_LABELS[key] ? getNativeSymbol(key) : null;
              return (
              <tr key={key} className="border-b dark:border-gray-700 last:border-0">
                <td className="px-4 py-2 font-medium text-gray-900 dark:text-white capitalize">
                  {STRATEGY_LABELS[key] || CHAIN_LABELS[key] || key}
                </td>
                <td className="px-2 py-2 text-right">{s.active || 0}</td>
                <td className="px-2 py-2 text-right">{s.closed || 0}</td>
                {nativeByKey && (
                  <td className="px-2 py-2 text-right font-mono text-xs text-gray-700 dark:text-gray-300">
                    {sym ? `${formatNum(s.totalInvested, 3)} ${sym}` : formatNum(s.totalInvested, 4)}
                  </td>
                )}
                <td className={`px-2 py-2 text-right ${(s.winRate || 0) >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
                  {s.winRate || 0}%
                </td>
                <td className={`px-2 py-2 text-right font-medium ${(s.totalPnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(s.totalPnl || 0) >= 0 ? '+' : ''}{formatNum(s.totalPnl, sym ? 3 : 4)}
                  {sym ? ` ${sym}` : ''}
                  <span className="text-gray-400 font-normal text-[10px] ml-1">
                    ({(s.totalPnlPercent || 0) >= 0 ? '+' : ''}{s.totalPnlPercent || 0}%)
                  </span>
                </td>
                <td className="px-2 py-2 text-right text-xs text-gray-500">{s.todayTrades || 0}</td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TradeRow({ p, onChart }) {
  const strat = getStrategyMeta(p);
  const isLive = p.isLive ?? p.executionMode === 'live';
  return (
    <tr className="border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => onChart(p)}>
      <td className="px-4 py-2">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-gray-900 dark:text-white">{p.tokenSymbol}</span>
          <span className={`px-1 py-0.5 text-[8px] font-bold rounded ${strat.badge}`}>{strat.label}</span>
        </div>
      </td>
      <td className="px-2 py-2">
        <span className={`px-1.5 py-0.5 text-[10px] rounded bg-${CHAIN_COLORS[p.chain] || 'gray'}-100 dark:bg-${CHAIN_COLORS[p.chain] || 'gray'}-900/30 text-${CHAIN_COLORS[p.chain] || 'gray'}-800 dark:text-${CHAIN_COLORS[p.chain] || 'gray'}-300`}>
          {CHAIN_LABELS[p.chain] || p.chain}
        </span>
      </td>
      <td className="px-2 py-2 text-center">
        <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
          isLive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
        }`}>
          {isLive ? 'LIVE' : 'DRY'}
        </span>
      </td>
      <td className="px-2 py-2 text-center text-xs capitalize">{p.status}</td>
      <td className="px-2 py-2 text-right font-mono text-xs">
        {formatNum(p.totalInvested, 3)} {getNativeSymbol(p.chain)}
      </td>
      <td className="px-2 py-2 text-right font-mono text-xs text-green-600">{formatNum(p.totalRealized, 4)}</td>
      <td className={`px-2 py-2 text-right font-medium ${(p.totalPnl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        <div>{(p.totalPnl || 0) >= 0 ? '+' : ''}{formatNum(p.totalPnl, 3)} {getNativeSymbol(p.chain)}</div>
        <div className="text-[10px]">{(p.totalPnlPercent || 0) >= 0 ? '+' : ''}{p.totalPnlPercent || 0}%</div>
      </td>
      <td className="px-2 py-2 text-right text-xs text-gray-400">{formatTime(p.closedAt || p.openedAt)}</td>
    </tr>
  );
}

export default function MasterChefAnalytics() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartPosition, setChartPosition] = useState(null);
  const [exitConfig, setExitConfig] = useState({});
  const [mode, setMode] = useDefaultTradingMode('', searchParams.get('mode'));
  const [chain, setChain] = useState('');
  const [strategy, setStrategy] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    api.get('/masterchef/dashboard').then((r) => setExitConfig(r.data.exitConfig || {})).catch(() => {});
  }, []);

  const sellPosition = async (id, percent) => {
    const confirmMsg = percent === 100
      ? 'Close this position (sell 100%)?'
      : `Sell ${percent}% of remaining tokens?`;
    if (!window.confirm(confirmMsg)) return null;
    try {
      const res = await api.post(`/masterchef/positions/${id}/sell`, { percent });
      if (res.data.txHash) {
        alert(`Sold ${percent}%! TX: ${res.data.txHash}`);
      }
      await fetchAnalytics();
      return res.data.position || null;
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to sell position');
      return null;
    }
  };

  const fetchAnalytics = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit, skip: page * limit, sort: 'openedAt' });
      if (mode) params.set('mode', mode);
      if (chain) params.set('chain', chain);
      if (strategy) params.set('strategy', strategy);
      if (status) params.set('status', status);
      const res = await api.get(`/masterchef/analytics?${params}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [mode, chain, strategy, status, page]);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  useEffect(() => { setPage(0); }, [mode, chain, strategy, status]);

  if (loading && !data) {
    return <div className="text-center py-12 text-gray-500">Loading analytics...</div>;
  }

  const summary = data?.summary || {};
  const trades = data?.trades || { positions: [], total: 0 };
  const totalPages = Math.ceil((trades.total || 0) / limit);

  return (
    <div className="space-y-6">
      <PortfolioPanel />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ModeStatsCard title="Live Trades" accentColor="green" stats={summary.live} byChain={data?.byChain?.live} />
        <ModeStatsCard title="Dry Run" accentColor="yellow" stats={summary.dry} byChain={data?.byChain?.dry} />
        <ModeStatsCard title="Combined" accentColor="blue" stats={summary.combined} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BreakdownTable title="Live — By Strategy" data={data?.byStrategy?.live} />
        <BreakdownTable title="Dry Run — By Strategy" data={data?.byStrategy?.dry} />
        <BreakdownTable title="Live — By Chain" data={data?.byChain?.live} nativeByKey />
        <BreakdownTable title="Dry Run — By Chain" data={data?.byChain?.dry} nativeByKey />
      </div>

      {(data?.bestTrades?.length > 0 || data?.worstTrades?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.bestTrades?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-green-600 mb-3">Best Trades</h3>
              <div className="space-y-2">
                {data.bestTrades.map((p) => (
                  <button key={p._id} onClick={() => setChartPosition(p)}
                    className="w-full flex items-center justify-between text-sm p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left">
                    <span className="font-medium">{p.tokenSymbol}</span>
                    <span className="text-green-600 font-bold">+{p.totalPnlPercent}%</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {data.worstTrades?.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-red-600 mb-3">Worst Trades</h3>
              <div className="space-y-2">
                {data.worstTrades.map((p) => (
                  <button key={p._id} onClick={() => setChartPosition(p)}
                    className="w-full flex items-center justify-between text-sm p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left">
                    <span className="font-medium">{p.tokenSymbol}</span>
                    <span className="text-red-600 font-bold">{p.totalPnlPercent}%</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {mode === 'live' ? 'Live Trades' : mode === 'dry_run' ? 'Dry Run Trades' : 'All Trades'}
            </h3>
            <p className="text-xs text-gray-500">{trades.total} total positions</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select value={mode} onChange={(e) => setMode(e.target.value)}
              className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="">All modes</option>
              <option value="live">Live</option>
              <option value="dry_run">Dry Run</option>
            </select>
            <select value={chain} onChange={(e) => setChain(e.target.value)}
              className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="">All chains</option>
              <option value="solana">Solana</option>
              <option value="base">Base</option>
              <option value="ethereum">Ethereum</option>
              <option value="bsc">BSC</option>
            </select>
            <select value={strategy} onChange={(e) => setStrategy(e.target.value)}
              className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="">All strategies</option>
              <option value="recovery">Recovery</option>
              <option value="pump">Pump</option>
              <option value="volume">Volume</option>
              <option value="ema">EMA</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="text-xs px-2 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="entering">Entering</option>
              <option value="closed">Closed</option>
              <option value="stopped">Stopped</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          {trades.positions.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">No trades match filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 sticky top-0">
                  <th className="text-left px-4 py-2">Token</th>
                  <th className="text-left px-2 py-2">Chain</th>
                  <th className="text-center px-2 py-2">Mode</th>
                  <th className="text-center px-2 py-2">Status</th>
                  <th className="text-right px-2 py-2">Size</th>
                  <th className="text-right px-2 py-2">Realized</th>
                  <th className="text-right px-2 py-2">P&L</th>
                  <th className="text-right px-2 py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {trades.positions.map((p) => (
                  <TradeRow key={p._id} p={p} onChart={setChartPosition} />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">
              Previous
            </button>
            <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 text-xs rounded bg-gray-100 dark:bg-gray-700 disabled:opacity-40">
              Next
            </button>
          </div>
        )}
      </div>

      <p className="text-xs text-gray-400">
        Manage open positions in <Link to="/trading/masterchef/positions" className="text-blue-500 hover:underline">Positions</Link>.
        View individual events in <Link to="/trading/masterchef/activity" className="text-blue-500 hover:underline">Activity</Link>.
      </p>

      {chartPosition && (
        <ChartModal
          position={chartPosition}
          onClose={() => setChartPosition(null)}
          onSell={sellPosition}
          exitConfig={exitConfig}
          onPositionUpdate={setChartPosition}
        />
      )}
    </div>
  );
}
